// Pair-endpoint snippet for Unreal / C++. Mirrors the Godot reference:
// persists the player token + install id alongside the install, exposes a
// game-instance subsystem so it survives map loads, and broadcasts
// delegates on success / failure.

export function buildPairUnrealSnippet(apiKey: string, functionsBaseUrl: string): string {
  return `// Source/<YourModule>/PlayerBasePairing.h + .cpp
// Drop the class into a project module. Build.cs deps:
//
//   PrivateDependencyModuleNames.AddRange(new[] { "HTTP", "Json", "OpenSSL" });
//
// Subsystem lifetime spans the whole game instance, so the token
// survives map transitions. Use UPlayerBasePairing::Get(this) anywhere
// you have a UObject (e.g. inside a Widget or Actor).
//
// Flow:
//   1. Player taps the recruitment button in Discord -> bot DMs them a
//      short code.
//   2. Your in-game pairing UI calls UPlayerBasePairing::Get(this)->Redeem(Code).
//   3. On success, OnPaired broadcasts the playerToken. The token plus
//      the install id are written to Saved/PlayerBase.json and reused
//      on every subsequent /events call (see PlayerBaseEvents).
//
// The install id is generated once per device and kept across re-pairs
// (Unpair() leaves it intact) so the backend can correlate sessions
// from the same install.

// --- PlayerBasePairing.h ----------------------------------------------------
#pragma once

#include "CoreMinimal.h"
#include "Subsystems/GameInstanceSubsystem.h"
#include "PlayerBasePairing.generated.h"

DECLARE_DYNAMIC_MULTICAST_DELEGATE_OneParam(FPlayerBasePaired, const FString&, PlayerToken);
DECLARE_DYNAMIC_MULTICAST_DELEGATE_OneParam(FPlayerBasePairFailed, const FString&, Reason);

UCLASS()
class UPlayerBasePairing : public UGameInstanceSubsystem
{
    GENERATED_BODY()
public:
    virtual void Initialize(FSubsystemCollectionBase& Collection) override;

    UFUNCTION(BlueprintCallable, Category = "PlayerBase")
    void Redeem(const FString& PairingCode);

    UFUNCTION(BlueprintCallable, Category = "PlayerBase")
    void Unpair();

    UFUNCTION(BlueprintPure, Category = "PlayerBase")
    bool IsPaired() const { return !PlayerToken.IsEmpty(); }

    UPROPERTY(BlueprintAssignable, Category = "PlayerBase")
    FPlayerBasePaired OnPaired;

    UPROPERTY(BlueprintAssignable, Category = "PlayerBase")
    FPlayerBasePairFailed OnPairFailed;

    const FString& GetPlayerToken() const { return PlayerToken; }
    const FString& GetInstallId() const { return InstallId; }

    static UPlayerBasePairing* Get(const UObject* WorldContext);

private:
    void LoadOrSeedStore();
    void Persist() const;

    FString PlayerToken;
    FString InstallId;
};

// --- PlayerBasePairing.cpp --------------------------------------------------
#include "PlayerBasePairing.h"

#include "HttpModule.h"
#include "Interfaces/IHttpRequest.h"
#include "Interfaces/IHttpResponse.h"
#include "Misc/FileHelper.h"
#include "Misc/Guid.h"
#include "Misc/Paths.h"
#include "Dom/JsonObject.h"
#include "Serialization/JsonReader.h"
#include "Serialization/JsonSerializer.h"

THIRD_PARTY_INCLUDES_START
#include <openssl/hmac.h>
THIRD_PARTY_INCLUDES_END

namespace
{
    static const FString ApiKey = TEXT("${apiKey}");
    static const FString PairUrl = TEXT("${functionsBaseUrl}/pair");

    FString StorePath()
    {
        return FPaths::ProjectSavedDir() / TEXT("PlayerBase.json");
    }

    FString HmacSha256Hex(const FString& Key, const FString& Body)
    {
        const FTCHARToUTF8 KeyUtf8(*Key);
        const FTCHARToUTF8 BodyUtf8(*Body);
        unsigned char Out[32];
        unsigned int OutLen = 0;
        HMAC(EVP_sha256(),
             KeyUtf8.Get(), KeyUtf8.Length(),
             reinterpret_cast<const unsigned char*>(BodyUtf8.Get()), BodyUtf8.Length(),
             Out, &OutLen);
        FString Hex;
        Hex.Reserve(OutLen * 2);
        const TCHAR* Digits = TEXT("0123456789abcdef");
        for (unsigned int i = 0; i < OutLen; ++i)
        {
            Hex.AppendChar(Digits[Out[i] >> 4]);
            Hex.AppendChar(Digits[Out[i] & 0xF]);
        }
        return Hex;
    }
}

UPlayerBasePairing* UPlayerBasePairing::Get(const UObject* WorldContext)
{
    if (!WorldContext) return nullptr;
    const UWorld* World = WorldContext->GetWorld();
    if (!World) return nullptr;
    return World->GetGameInstance()->GetSubsystem<UPlayerBasePairing>();
}

void UPlayerBasePairing::Initialize(FSubsystemCollectionBase& Collection)
{
    Super::Initialize(Collection);
    LoadOrSeedStore();
}

void UPlayerBasePairing::Unpair()
{
    PlayerToken.Empty();
    Persist();
}

void UPlayerBasePairing::Redeem(const FString& PairingCode)
{
    const FString Code = PairingCode.TrimStartAndEnd();

    const TSharedRef<FJsonObject> Json = MakeShared<FJsonObject>();
    Json->SetStringField(TEXT("code"), Code);
    Json->SetStringField(TEXT("installId"), InstallId);
    FString Body;
    const TSharedRef<TJsonWriter<>> Writer = TJsonWriterFactory<>::Create(&Body);
    FJsonSerializer::Serialize(Json, Writer);

    const FString Signature = HmacSha256Hex(ApiKey, Body);

    TSharedRef<IHttpRequest, ESPMode::ThreadSafe> Request = FHttpModule::Get().CreateRequest();
    Request->SetURL(PairUrl);
    Request->SetVerb(TEXT("POST"));
    Request->SetHeader(TEXT("content-type"), TEXT("application/json"));
    Request->SetHeader(TEXT("x-playerbase-signature"), Signature);
    Request->SetContentAsString(Body);

    TWeakObjectPtr<UPlayerBasePairing> WeakThis(this);
    Request->OnProcessRequestComplete().BindLambda(
        [WeakThis](FHttpRequestPtr, FHttpResponsePtr Response, bool bSuccess)
        {
            UPlayerBasePairing* Self = WeakThis.Get();
            if (!Self) return;
            if (!bSuccess || !Response.IsValid())
            {
                Self->OnPairFailed.Broadcast(TEXT("transport"));
                return;
            }
            if (Response->GetResponseCode() != 200)
            {
                FString Reason = FString::Printf(TEXT("http_%d"), Response->GetResponseCode());
                TSharedPtr<FJsonObject> Err;
                const TSharedRef<TJsonReader<>> Reader = TJsonReaderFactory<>::Create(Response->GetContentAsString());
                if (FJsonSerializer::Deserialize(Reader, Err) && Err.IsValid())
                {
                    FString ErrField;
                    if (Err->TryGetStringField(TEXT("error"), ErrField)) Reason = ErrField;
                }
                Self->OnPairFailed.Broadcast(Reason);
                return;
            }
            TSharedPtr<FJsonObject> Parsed;
            const TSharedRef<TJsonReader<>> Reader = TJsonReaderFactory<>::Create(Response->GetContentAsString());
            FString Token;
            if (!FJsonSerializer::Deserialize(Reader, Parsed) || !Parsed.IsValid()
                || !Parsed->TryGetStringField(TEXT("playerToken"), Token) || Token.IsEmpty())
            {
                Self->OnPairFailed.Broadcast(TEXT("bad_response"));
                return;
            }
            Self->PlayerToken = Token;
            Self->Persist();
            Self->OnPaired.Broadcast(Token);
        });
    if (!Request->ProcessRequest())
    {
        OnPairFailed.Broadcast(TEXT("transport"));
    }
}

void UPlayerBasePairing::LoadOrSeedStore()
{
    FString Raw;
    if (FFileHelper::LoadFileToString(Raw, *StorePath()))
    {
        TSharedPtr<FJsonObject> Parsed;
        const TSharedRef<TJsonReader<>> Reader = TJsonReaderFactory<>::Create(Raw);
        if (FJsonSerializer::Deserialize(Reader, Parsed) && Parsed.IsValid())
        {
            Parsed->TryGetStringField(TEXT("playerToken"), PlayerToken);
            Parsed->TryGetStringField(TEXT("installId"), InstallId);
        }
    }
    if (InstallId.IsEmpty())
    {
        InstallId = FGuid::NewGuid().ToString(EGuidFormats::DigitsWithHyphensLower);
        Persist();
    }
}

void UPlayerBasePairing::Persist() const
{
    const TSharedRef<FJsonObject> Json = MakeShared<FJsonObject>();
    Json->SetStringField(TEXT("playerToken"), PlayerToken);
    Json->SetStringField(TEXT("installId"), InstallId);
    FString Out;
    const TSharedRef<TJsonWriter<>> Writer = TJsonWriterFactory<>::Create(&Out);
    FJsonSerializer::Serialize(Json, Writer);
    FFileHelper::SaveStringToFile(Out, *StorePath());
}
`;
}
